import * as angular from 'angular';
import * as _ from 'lodash';
import { Dictionary, Group } from '../../models';
import { IGroupService } from '../data';

export interface ISmartGroupSelectService {
    smartGroupSelect(guid: string, types: string[], groups: Dictionary<Group>): angular.IPromise<Group>;
}

export class SmartGroupSelectService implements ISmartGroupSelectService {
    static serviceId = 'smartGroupSelectService';
    static $inject = ['$q', 'groupService'];

    constructor(
        private _$q: angular.IQService,
        private _groupService: IGroupService
    ) {
    }

    /**
     * Find an appropriate group using some search given dashboard types
     * Defined by story #34515
     * 
     * @param guid: the base group suggested
     * @param types: the types used by the dashboard
     * @param groups: a guid dictionary of all groups
     * @returns the selected group
     */
    smartGroupSelect(guid: string, types: string[], groups: Dictionary<Group>): angular.IPromise<Group> {
        const result = this._$q.defer<Group>();
        const all = _.values(groups);
        const found = groups[guid];

        // found but not compatible type
        if (found && types && types.indexOf(found.type) < 0) {

            // 1. in children (if exist) of the chosen group
            //    if there is a children that have a compatible type (stop at first found).
            //    Note order is all first generation siblings, then all second generation siblings, ...
            let children = this.getDirectChildren([found], all);
            while (children.length > 0) {
                // if there is a children that have a compatible type (stop at first found).
                const child = _.find(children, c => this.hasCompatibleType(c, types));
                if (child) {
                    // it's the choosen one, return it
                    result.resolve(child);
                    break;
                }
                children = this.getDirectChildren(children, all);
            }

            // 2. if not found in children, check in parents of chosen group for the first parent
            //    with a type group being the one expected by the target dashboard
            let parent = groups[found.parentGUID];
            while (parent) {
                if (this.hasCompatibleType(parent, types)) {
                    // it's the choosen one, return it
                    result.resolve(parent);
                    break;
                }
                parent = groups[parent.parentGUID];
            }

            // 3. looks in siblings 
            // same algo as for finding a group from python scripts(Values method) is used 
            // (we take the first closest relative) with the difference that 
            // we show the first one if multiple are at the same level (in script it generates an error)
            // and that we use types not name
            this._groupService
                .getClosestByType(found.guid, types)
                .then(guid => result.resolve(groups[guid]));
        }
        else {
            result.resolve(found);
        }

        return result.promise;
    }

    private hasCompatibleType(group: Group, types: string[]): boolean {
        return types.indexOf(group.type) >= 0;
    }

    private getDirectChildren(groups: Group[], all: Group[]): Group[] {
        const byParent = _.groupBy(all, g => g.parentGUID);
        return _.flatMap(groups, g => byParent[g.guid]).filter(g => g !== undefined);
    }
}